import type BABYLON from 'babylonjs';
import type MATERIALS from 'babylonjs-materials';

import type { Globals } from './globals';

export function updateMeshesCustomShader(
  globals: Globals,
  meshes: BABYLON.AbstractMesh[],
  {
    fragmentBeforeFragColor,
    materialDefines,
  }: {
    fragmentBeforeFragColor?: string;
    materialDefines?: Record<string, number>;
  } = {}
): void {
  meshes.forEach((mesh) => {
    if (
      fragmentBeforeFragColor &&
      mesh.material instanceof globals.BABYLON.PBRMaterial &&
      !(mesh.material instanceof globals.MATERIALS.PBRCustomMaterial)
    ) {
      const originalMat = mesh.material;
      const customMat = globals.BABYLON.SerializationHelper.Clone(
        () => new globals.MATERIALS.PBRCustomMaterial(originalMat.name, originalMat.getScene()),
        originalMat as MATERIALS.PBRCustomMaterial
      );
      customMat.id = originalMat.id;
      customMat.name = originalMat.name;

      originalMat.stencil.copyTo(customMat.stencil);
      originalMat.clearCoat.copyTo(customMat.clearCoat);
      originalMat.anisotropy.copyTo(customMat.anisotropy);
      originalMat.brdf.copyTo(customMat.brdf);
      originalMat.sheen.copyTo(customMat.sheen);
      originalMat.subSurface.copyTo(customMat.subSurface);

      customMat.Fragment_Before_FragColor(fragmentBeforeFragColor);

      originalMat.getBindedMeshes().forEach((m) => {
        // eslint-disable-next-line no-param-reassign
        m.material = customMat;
      });
      originalMat.dispose();
    }
    if (mesh.material instanceof globals.MATERIALS.PBRCustomMaterial) {
      if (materialDefines) {
        // eslint-disable-next-line no-param-reassign
        mesh.material.customShaderNameResolve = function customShaderNameResolve(
          shaderName,
          uniforms,
          uniformBuffers,
          samplers,
          defines,
          attribs,
          csnrOptions
        ): string {
          if (!Array.isArray(defines)) {
            Object.assign(defines, materialDefines);
            if (
              Object.keys(materialDefines).some(
                (k) =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle
                  !(k in (defines as any)._keys)
              )
            ) {
              defines.rebuild();
            }
          }
          return this.Builder(
            shaderName,
            uniforms,
            uniformBuffers,
            samplers,
            defines,
            attribs,
            csnrOptions
          );
        };
      }
    }
  });
}

export function createShaderConditionFragment(
  codeSet: Record<string | number, (string | undefined)[]>,
  elseCodes?: (string | undefined)[]
): string {
  const fragments: string[] = [];
  Object.keys(codeSet).forEach((condition) => {
    const codes = codeSet[condition];
    if (codes?.length) {
      fragments.push(
        `${fragments.length ? '#elif' : '#if'} ${condition}`,
        ...codes.filter((code): code is string => !!code)
      );
    }
  });
  const existsFragments = !!fragments.length;
  if (elseCodes?.length) {
    if (existsFragments) fragments.push('#else');
    fragments.push(...elseCodes.filter((code): code is string => !!code));
  }
  if (existsFragments) fragments.push('#endif');
  return fragments.join(`\n`);
}

export function createMeshesCustomShaderOptions<T extends string>(
  defineName: string,
  settings: { name: T; finalColor?: string; condition?: string; beforeFragment?: string }[]
): [defines: Record<T, number>, fragment: string] {
  const defines = {} as Record<T, number>;
  const codeSet: Record<string | number, (string | undefined)[]> = {};
  settings.forEach(({ name, finalColor, condition, beforeFragment }, i) => {
    defines[name] = i;
    if (finalColor) {
      const codes = [beforeFragment, `finalColor=${finalColor};`];
      codeSet[`${defineName} == ${i}`] = condition
        ? [
            createShaderConditionFragment(
              {
                [condition]: codes,
              },
              [`finalColor=vec4(1,0,1,1);`]
            ),
          ]
        : codes;
    }
  });
  return [defines, createShaderConditionFragment(codeSet)];
}
